// Admin modal for per-category settings. Each of the 14 shop types is listed
// with its own row; owner picks:
//   - Default unit (piece / kg / gram / liter / bundle / box)
//   - Allowed units (multi-select — what shows up in the menu-item form)
// The store is `db.category_profiles` (backend/routers/category_profiles.py).
// Save is per-row (PATCH /admin/category-profiles/{shop_type}) so admins can
// tweak one category at a time without touching the rest.
import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_TILES } from "./CategoryGrid";

// Unit labels used across the settings UI + menu-editor unit selector.
export const UNIT_LABELS = {
  piece:    { ar: "حبة",   short: "حبة" },
  kilogram: { ar: "كيلو",  short: "كغ" },
  gram:     { ar: "جرام",  short: "غ" },
  liter:    { ar: "لتر",   short: "لتر" },
  bundle:   { ar: "باقة",  short: "باقة" },
  box:      { ar: "علبة",  short: "علبة" },
};

// Include the "other" tile in settings, plus a friendly Arabic label for
// every shop_type value we return from the backend.
const CATEGORY_META = [
  ...CATEGORY_TILES,
  { value: "other", label: "أي شيء آخر", bg: "bg-slate-500" },
  { value: "sweets", label: "حلويات (قديم)", bg: "bg-pink-500" },
];

function labelFor(shop_type) {
  return CATEGORY_META.find((c) => c.value === shop_type)?.label || shop_type;
}
function bgFor(shop_type) {
  return CATEGORY_META.find((c) => c.value === shop_type)?.bg || "bg-slate-500";
}

export default function AdminCategorySettingsModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState(Object.keys(UNIT_LABELS));
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/category-profiles");
      setRows(data.profiles || []);
      if (Array.isArray(data.units) && data.units.length) setUnits(data.units);
    } catch (e) { setErr(extractError(e) || "تعذّر تحميل الإعدادات"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (shop_type, patch) => {
    setRows((prev) => prev.map((r) => r.shop_type === shop_type ? { ...r, ...patch } : r));
  };

  const toggleAllowed = (shop_type, unit) => {
    const row = rows.find((r) => r.shop_type === shop_type);
    const cur = new Set(row?.allowed_units || []);
    if (cur.has(unit)) {
      // Never let the list become empty — must keep at least 1.
      if (cur.size <= 1) return;
      cur.delete(unit);
    } else {
      cur.add(unit);
    }
    // Keep canonical order.
    const next = units.filter((u) => cur.has(u));
    update(shop_type, { allowed_units: next });
  };

  const save = async (row) => {
    setSavingKey(row.shop_type); setErr("");
    try {
      const { data } = await api.patch(`/admin/category-profiles/${row.shop_type}`, {
        default_unit: row.default_unit,
        allowed_units: row.allowed_units,
      });
      update(row.shop_type, data);
      toast.success(`تم حفظ إعدادات ${labelFor(row.shop_type)}`, { duration: 2500 });
    } catch (e) { setErr(extractError(e) || "تعذّر الحفظ"); }
    finally { setSavingKey(null); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="category-settings-modal"
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><Settings size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:"Cairo"}}>إعدادات الأقسام</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">وحدة القياس الافتراضية + الوحدات المسموحة لكل نوع محل</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center" data-testid="cat-settings-close">
            <X size={16}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && <div className="text-center py-6 text-sm text-[var(--text-secondary)]">جاري التحميل…</div>}
          {err && <div className="bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}
          {!loading && rows.map((row) => {
            const allowed = new Set(row.allowed_units || []);
            const isSaving = savingKey === row.shop_type;
            return (
              <div key={row.shop_type} className="bg-[var(--surface-alt)] rounded-2xl p-3" data-testid={`cat-row-${row.shop_type}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-xl ${bgFor(row.shop_type)} text-white flex items-center justify-center font-black`}>
                    {labelFor(row.shop_type).charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-sm truncate" style={{fontFamily:"Cairo"}}>{labelFor(row.shop_type)}</div>
                    <div className="text-[10px] text-[var(--text-secondary)]">{row.shop_type}</div>
                  </div>
                  <button
                    onClick={() => save(row)}
                    disabled={isSaving}
                    className="shrink-0 px-3 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-black flex items-center gap-1 disabled:opacity-60"
                    data-testid={`cat-save-${row.shop_type}`}
                  >
                    <Save size={12}/> {isSaving ? "..." : "حفظ"}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-black text-[var(--text-secondary)] block mb-1">الوحدة الافتراضية</label>
                    <select
                      value={row.default_unit}
                      onChange={(e) => update(row.shop_type, { default_unit: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-[var(--border)] bg-white text-sm font-bold"
                      data-testid={`cat-default-${row.shop_type}`}
                    >
                      {units.map((u) => (
                        <option key={u} value={u}>{UNIT_LABELS[u]?.ar || u}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-[var(--text-secondary)] block mb-1">الوحدات المسموحة</label>
                    <div className="flex flex-wrap gap-1">
                      {units.map((u) => {
                        const on = allowed.has(u);
                        const isDefault = row.default_unit === u;
                        return (
                          <button
                            key={u}
                            type="button"
                            onClick={() => toggleAllowed(row.shop_type, u)}
                            disabled={isDefault}   // default must stay in allowed
                            className={`px-2 py-1 rounded-full text-[11px] font-black border transition ${
                              on
                                ? "bg-primary-brand text-white border-primary-brand"
                                : "bg-white text-[var(--text-secondary)] border-[var(--border)] hover:border-primary-brand/60"
                            } ${isDefault ? "opacity-60 cursor-not-allowed" : ""}`}
                            title={isDefault ? "الوحدة الافتراضية — لا يمكن إخفاؤها" : ""}
                            data-testid={`cat-allowed-${row.shop_type}-${u}`}
                          >
                            {UNIT_LABELS[u]?.ar || u}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
